import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { navigateToCollectionScanner } from '../../scan/scanModalNavigation';
import { useAuth } from '../../hooks/useAuth';
import { usePendingScanTip } from '../../hooks/usePendingScanTip';
import { NavCountBadge } from './NavCountBadge';
import { isStaleDeployError, recoverFromStaleDeploy } from '../../utils/pwaReload';
import {
  mobileTabIdForRootPath,
  MOBILE_TAB_ROOTS,
  rememberMobileTabPath,
  rememberedMobileTabPath,
} from './mobileTabStacks';
import { BELOW_DESKTOP_MEDIA } from './layoutConstants';
import {
  mobileTabRoutes,
  MOBILE_SCAN_TAB,
  prefetchRouteDestination,
} from './routeMetadata';

/** Reserve space for scroll / FAB (AlterSearch-style thumb zone). */
export const MOBILE_TAB_BAR_HEIGHT = 56;

/**
 * Bottom inset while the tab bar is the floating tablet pill (`md` until `lg`).
 * Phone height, plus the pill's offset from the screen edge and a gap so the
 * last controls clear it. Same inset as the tablet shell padding in AppLayout.
 */
export const TABLET_TAB_BAR_CLEARANCE_PX = MOBILE_TAB_BAR_HEIGHT + 28;

/**
 * Extra scroll room above the fixed tab bar so the filter FAB does not cover
 * the last grid row (matches AppLayout / keep-alive content padding).
 */
export const MOBILE_FILTERS_FAB_CLEARANCE_PX = 68;

/**
 * Bottom padding for mobile scroll content so the last rows clear the fixed
 * tab bar (and optional filter FAB). Used by AppLayout’s shell padding and by
 * {@link MobileTabKeepAlive}’s in-flow content wrapper — keep-alive panels are
 * `position: absolute`, so shell padding alone does not extend window scroll.
 */
export function mobileContentBottomPadding(opts: {
  tabs: boolean;
  filtersFab: boolean;
}): string {
  const safe = 'env(safe-area-inset-bottom, 0px)';
  if (opts.tabs && opts.filtersFab) {
    return `calc(${MOBILE_TAB_BAR_HEIGHT + MOBILE_FILTERS_FAB_CLEARANCE_PX}px + ${safe})`;
  }
  if (opts.tabs) {
    return `calc(${MOBILE_TAB_BAR_HEIGHT + 16}px + ${safe})`;
  }
  if (opts.filtersFab) {
    return `calc(${MOBILE_FILTERS_FAB_CLEARANCE_PX + 28}px + ${safe})`;
  }
  return '16px';
}

/**
 * Below modal sheets (`SHEET_Z_FILTER` = 1300) so open sheets cover the tab bar
 * like native iOS/Android. Idle filter FAB sits just above the tabs.
 */
export const MOBILE_TAB_BAR_Z = 1250;
/** Slightly above the tab bar for the bottom-right corner overlap with the last tab. */
export const MOBILE_FILTER_FAB_Z = 1260;
/** Above an open filter sheet so the tune/close FAB remains tappable. */
export const MOBILE_FILTER_FAB_OPEN_Z = 1310;

/** Width/height shared by tune/add fixed corner FABs (filters, deck gallery). */
export const MOBILE_ACTION_FAB_SIZE_PX = 52;

const tabs = mobileTabRoutes().map((route) => ({
  to: route.to,
  label: route.label,
  icon: route.mobileTab!.icon,
  iconActive: route.mobileTab!.iconActive,
  match: route.match,
  prefetch: route.mobileTab!.prefetch,
}));

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pathname = location.pathname;
  const scanTipCount = usePendingScanTip(pathname, Boolean(user));
  const regularTabs = tabs;
  // Split around the centre slot: Cards + Collection | Scan | Decks + Team
  const leftTabs = regularTabs.slice(0, 2);
  const rightTabs = regularTabs.slice(2);

  // Keep each tab's last browse path so switching tabs can restore it.
  useEffect(() => {
    rememberMobileTabPath(location);
  }, [location]);

  // Warm bottom-nav destinations during idle so the first tab switch does not
  // wait on a chunk fetch. The bar is hidden from `lg` up — skip there so
  // desktop sessions do not download unused tab trees. Push screens (deck
  // builder, match result) stay on-demand unless a remembered tab path needs them.
  useEffect(() => {
    if (!window.matchMedia(BELOW_DESKTOP_MEDIA).matches) return;

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const prefetch = () => {
      for (const tab of tabs) {
        const tabId = mobileTabIdForRootPath(tab.to);
        if (!tabId) continue;
        void prefetchRouteDestination(tab.prefetch, rememberedMobileTabPath(tabId)).catch((err) => {
          if (isStaleDeployError(err)) void recoverFromStaleDeploy();
        });
      }
      void import('../../views/CollectionScanPage').catch((err) => {
        if (isStaleDeployError(err)) void recoverFromStaleDeploy();
      });
      void import('../../scan/loadCardCatalog').then((m) => m.loadCardCatalog()).catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(prefetch, { timeout: 8000 });
    } else {
      timeoutId = window.setTimeout(prefetch, 3000);
    }
    return () => {
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  const handleScanTap = useCallback(() => {
    // Already scanning: opening a second camera capture here would leak its
    // tracks (the mounted scan page never re-takes the stash) and can make
    // getUserMedia fail with a busy camera on some devices.
    // Present as a fullScreen modal over the current tab (keeps underlay mounted).
    navigateToCollectionScanner(navigate, location);
  }, [location, navigate]);

  const handleTabClick = useCallback(
    async (to: string, active: boolean, prefetch: () => Promise<unknown>) => {
      if (to === '/collection' && scanTipCount > 0 && !active) {
        navigate('/collection?justScanned=1', { replace: true });
        return;
      }

      const targetTab = mobileTabIdForRootPath(to);
      if (!targetTab) {
        navigate(to);
        return;
      }

      // Re-tap active tab → pop to that tab's root (iOS UITabBar pattern).
      if (active) {
        const root = MOBILE_TAB_ROOTS[targetTab];
        if (pathname !== root || location.search) {
          navigate(root, { replace: true });
        }
        return;
      }

      rememberMobileTabPath(location);
      const dest = rememberedMobileTabPath(targetTab);
      // Ensure route chunk(s) are ready before swapping — avoids Suspense
      // replacing the outgoing tab with a spinner on a cold first visit.
      try {
        await prefetchRouteDestination(prefetch, dest);
      } catch (err) {
        if (isStaleDeployError(err)) {
          void recoverFromStaleDeploy();
          return;
        }
        // Navigation still proceeds; RouteSuspenseFallback is the fallback.
      }

      // Tab bar swaps are parallel stack restores, not pushes — replace so
      // browser back doesn't walk through other tabs, and the route transition
      // layer treats this as a shell swap (no slide).
      navigate(dest, { replace: true });
    },
    [location, navigate, pathname, scanTipCount],
  );

  const renderTab = ({ to, label, icon: IconInactive, iconActive: IconActive, match, prefetch }: typeof regularTabs[number]) => {
    const active = match(pathname);
    const color = active ? 'accent' : 'textMuted';
    const IconComp = active ? IconActive : IconInactive;
    return (
      <Box
        as="button"
        key={to}
        onPointerDown={() => {
          void prefetch();
        }}
        onClick={() => {
          void handleTabClick(to, active, prefetch);
        }}
        aria-current={active ? 'page' : undefined}
        minH="44px"
        flex={{ base: 1, md: '0 0 auto' }}
        minW={{ base: 0, md: '4.25rem' }}
        WebkitTapHighlightColor="transparent"
        touchAction="manipulation"
        _active={{ opacity: 0.72, transform: 'scale(0.96)' }}
        css={{
          '@media (prefers-reduced-motion: reduce)': {
            '&:active': { transform: 'none', opacity: 0.85 },
          },
        }}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
      >
        <VStack gap={0} align="center" justify="center" minH="44px" py={1}>
          <Box
            position="relative"
            color={color}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <IconComp size={20} width={20} height={20} aria-hidden />
            {to === '/collection' ? <NavCountBadge count={scanTipCount} /> : null}
          </Box>
          <Text fontSize="2xs" fontWeight={active ? 'semibold' : 'normal'} color={color} lineClamp={1}>
            {label}
          </Text>
        </VStack>
      </Box>
    );
  };

  return (
    <Box
      display={{ base: 'block', lg: 'none' }}
      position="fixed"
      zIndex={MOBILE_TAB_BAR_Z}
      bottom={{ base: 0, md: 'max(12px, env(safe-area-inset-bottom, 0px))' }}
      left={{ base: 0, md: '50%' }}
      right={{ base: 0, md: 'auto' }}
      transform={{ base: 'none', md: 'translateX(-50%)' }}
      w={{ md: 'max-content' }}
      maxW={{ md: 'calc(100vw - 2rem)' }}
      bg={{ base: 'bgElevated', md: 'bg/80' }}
      borderTopWidth="hairline"
      borderWidth={{ md: 'hairline' }}
      borderColor="border"
      borderRadius={{ base: 0, md: 'full' }}
      boxShadow={{ md: 'lg' }}
      px={{ base: 2, md: 3 }}
      pt={2}
      pb={{
        base: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        md: 2,
      }}
      css={{
        '@media (min-width: 48em)': {
          backdropFilter: 'blur(18px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.4)',
        },
      }}
    >
      <HStack as="nav" justify="space-around" align="center" gap={0} aria-label="Primary">
        {leftTabs.map(renderTab)}

        <ScanTab pathname={pathname} onTap={handleScanTap} />

        {rightTabs.map(renderTab)}
      </HStack>
    </Box>
  );
}

function ScanTab({ pathname, onTap }: { pathname: string; onTap: () => void }) {
  const active = pathname === '/collection/scan';
  const color = active ? 'accent' : 'textMuted';
  const IconComp = active ? MOBILE_SCAN_TAB.iconActive : MOBILE_SCAN_TAB.icon;
  return (
    <Box
      as="button"
      flex={{ base: 1, md: '0 0 auto' }}
      minW={{ base: 0, md: '4.25rem' }}
      minH="44px"
      onClick={onTap}
      aria-label="Scan cards with camera"
      WebkitTapHighlightColor="transparent"
      touchAction="manipulation"
      _active={{ opacity: 0.72, transform: 'scale(0.96)' }}
      css={{
        '@media (prefers-reduced-motion: reduce)': {
          '&:active': { transform: 'none', opacity: 0.85 },
        },
      }}
      style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
    >
      <VStack gap={0} align="center" justify="center" minH="44px" py={1}>
        <Box color={color} display="flex" alignItems="center" justifyContent="center">
          <IconComp size={20} aria-hidden />
        </Box>
        <Text fontSize="2xs" fontWeight={active ? 'semibold' : 'normal'} color={color} lineClamp={1}>
          Scan
        </Text>
      </VStack>
    </Box>
  );
}
